import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ListingCard } from '../components/ListingCard';
import { listingsApi, categoriesApi } from '../lib/api';
import { Search, Loader2, Filter, X } from 'lucide-react';

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await categoriesApi.getAll();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCategory && selectedCategory !== 'all') {
          params.category = selectedCategory;
        }
        if (search) {
          params.search = search;
        }
        const { data } = await listingsApi.getAll(params);
        setListings(data);
      } catch (error) {
        console.error('Failed to fetch listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [selectedCategory, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (search) params.set('search', search);
    setSearchParams(params);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (search) params.set('search', search);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSearchParams({});
  };

  const hasFilters = selectedCategory !== 'all' || search;

  return (
    <div className="min-h-screen py-8" data-testid="browse-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Browse Listings
          </h1>
          <p className="text-[15px] text-muted-foreground">
            Find what you're looking for in the Habesha community
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search listings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">
              Search
            </Button>
          </form>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`category-pill ${
                selectedCategory === 'all' ? 'category-pill-active' : 'category-pill-inactive'
              }`}
              data-testid="category-all"
            >
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`category-pill ${
                  selectedCategory === cat.id ? 'category-pill-active' : 'category-pill-inactive'
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Active Filters */}
          {hasFilters && (
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Active filters:</span>
              {selectedCategory !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {categories.find(c => c.id === selectedCategory)?.name}
                </span>
              )}
              {search && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  "{search}"
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : listings.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {listings.length} listing{listings.length !== 1 ? 's' : ''} found
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="listings-grid">
              {listings.map((listing, index) => (
                <div key={listing.id} className={`animate-fade-in stagger-${(index % 4) + 1}`}>
                  <ListingCard listing={listing} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 bg-muted/50 rounded-2xl" data-testid="no-results">
            <p className="text-muted-foreground mb-2">No listings found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filters
            </p>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-4">
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
